import React from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import {
  useGetUserDecorationsQuery,
  useSelectUserDecorationMutation,
} from '@/api/queries/user';
import { playSound } from '@/common/lib/soundEffect';
import {
  getDecorationClass,
  getDecorationTitleKey,
  NO_DECORATION_ID,
  NO_DECORATION_TITLE,
} from '@/components/Leagues/PositionItem/decorations/registry';
import { useAppSelector } from '@/redux/store';

import css from './style.module.scss';

const ChangeSkin: React.FC = () => {
  const { t } = useTranslation();
  const me = useAppSelector((state) => state.user.user);
  const { data, isLoading } = useGetUserDecorationsQuery();
  const [selectSkin, { isLoading: isSaving }] =
    useSelectUserDecorationMutation();

  // «Стандартная» идёт последней: это снятие украшения, а не приз.
  const options = [
    ...(data?.items ?? []).map((item) => ({
      id: item.decorationId,
      title: t(getDecorationTitleKey(item.decorationId)),
    })),
    { id: NO_DECORATION_ID, title: t(NO_DECORATION_TITLE) },
  ];

  const activeId = data?.activeId ?? NO_DECORATION_ID;
  const displayName =
    [me?.firstName, me?.lastName].filter(Boolean).join(' ') ||
    me?.userName ||
    '';

  const handleSelect = (id: string): void => {
    if (isSaving || id === activeId) return;

    playSound('buttons/light');
    selectSkin(id);
  };

  return (
    <main className="page">
      <div className={css.skins}>
        <h2 className={css.title}>{t('Profile skin')}</h2>
        <p className={css.hint}>
          {t('The chosen skin decorates your row in every leaderboard.')}
        </p>

        {isLoading ? (
          <div className={css.placeholder}>{t('Loading')}</div>
        ) : (
          <ul className={css.list}>
            {options.map((option) => {
              const isActive = option.id === activeId;

              return (
                <li key={option.id || 'default'}>
                  <button
                    type="button"
                    className={cn(css.option, isSaving && css.optionBusy)}
                    aria-pressed={isActive}
                    onClick={(): void => handleSelect(option.id)}
                  >
                    <span className={css.caption}>
                      <span className={css.name}>{option.title}</span>
                      {isActive && (
                        <span className={css.badge}>{t('Selected')}</span>
                      )}
                    </span>

                    {/* Превью повторяет геометрию строки рейтинга (50px,
                        rounded-lg, та же сетка) и подставляет данные игрока:
                        видно ровно то, что увидят остальные. */}
                    <span
                      className={cn(css.row, isActive && css.rowActive)}
                      data-plate={option.id || undefined}
                    >
                      <span
                        className={cn(
                          css.plate,
                          getDecorationClass(option.id) ?? css.plateDefault,
                        )}
                      />
                      <span className={css.position}>1</span>
                      <span
                        className={css.avatar}
                        style={
                          me?.photoUrl
                            ? { backgroundImage: `url("${me.photoUrl}")` }
                            : undefined
                        }
                      />
                      <span className={css.identity}>
                        <span className={css.playerName}>{displayName}</span>
                        <span className={css.level}>
                          {`${t('lvl')} ${me?.level ?? 1}`}
                        </span>
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </main>
  );
};

export default ChangeSkin;
